import { UnderlineButton } from 'components/dynamic-content/button';
import React from 'react';

interface IProps {
  title: string;
  seeAll?: string;
  titleHref?: string;
  children: React.ReactNode;
  seeAllWidth?: string;
}

export const ListSection: React.FC<IProps> = ({ title, seeAll, titleHref, children, seeAllWidth }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [hasScroll, setHasScroll] = React.useState(false);
  const scrollIndicatorRef = React.useRef<HTMLDivElement>(null);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollProgress(progress);
      setHasScroll(maxScroll > 0);
    }
  };

  // Initialize scroll progress on mount
  React.useEffect(() => {
    handleScroll();
  }, []);

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Also check on resize
      window.addEventListener('resize', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
    return undefined;
  }, []);

  return (
    <div className='mb-8 flex flex-col gap-6 md:mb-16 md:gap-8'>
      <div className='flex w-full flex-row items-start justify-between gap-4'>
        <div className='flex flex-col items-start justify-start'>
          <div className='text-red-950 leading-trim-both text-edge-cap justify-start text-xl font-bold leading-[120%] sm:text-2xl md:text-3xl'>
            {title}
          </div>
        </div>
        {seeAll && titleHref && (
          <div className={`${seeAllWidth || 'w-24'} flex flex-col items-end justify-end`}>
            <UnderlineButton link={titleHref} text={seeAll} className='w-fit' underlineWidth='50' />
          </div>
        )}
      </div>
      <div className='scrollbar-hide w-full overflow-x-auto' ref={scrollContainerRef}>
        <div className='flex min-w-max flex-row gap-3 md:gap-4'>{children}</div>
      </div>
      {hasScroll && (
        <div ref={scrollIndicatorRef} className='relative block h-1 w-full rounded-[220px] bg-neutral-100 md:hidden'>
          <div
            className='absolute h-1 rounded-[60px] bg-eggplant-500 transition-all duration-300 ease-out'
            style={{
              width: '30%',
              left: `${scrollProgress * 70}%`,
              minWidth: '20%',
            }}
          />
        </div>
      )}
    </div>
  );
};
